import { Workflow as WorkflowTypes } from "./service/Workflow";
import { IngestionFieldDefinition } from "../data-ingestion/data.ingestion.service";

export class WorkflowUtil {
    /**
     * Populates stage names on transitions based on stage IDs
     * This is needed for validation when transitions haven't been loaded in the UI yet
     */
    static populateStageNamesOnTransitions(
        transitions: WorkflowTypes.WorkflowStageTransition[],
        stages: WorkflowTypes.WorkflowStage[],
    ): void {
        const stageIdToNameMap = new Map<number, string>();
        stages.forEach((stage) => {
            stageIdToNameMap.set(stage.workflowStageId, stage.stageName);
        });

        transitions.forEach((transition) => {
            if (!transition.fromStageName && transition.fromStageId) {
                transition.fromStageName = stageIdToNameMap.get(transition.fromStageId) || "";
            }
            if (!transition.toStageName && transition.toStageId) {
                transition.toStageName = stageIdToNameMap.get(transition.toStageId) || "";
            }
        });
    }

    /**
     * Private helper method for DFS cycle detection
     */
    private static detectCycleInGraph(adjacencyList: Map<string, string[]>): boolean {
        const visited = new Set<string>();
        const recursionStack = new Set<string>();

        const hasCycleDFS = (stageName: string): boolean => {
            if (recursionStack.has(stageName)) {
                return true; // Cycle detected
            }
            if (visited.has(stageName)) {
                return false; // Already processed
            }

            visited.add(stageName);
            recursionStack.add(stageName);

            const neighbors = adjacencyList.get(stageName) || [];
            for (const neighbor of neighbors) {
                if (hasCycleDFS(neighbor)) {
                    return true;
                }
            }

            recursionStack.delete(stageName);
            return false;
        };

        // Check for cycles starting from each stage
        for (const stageName of adjacencyList.keys()) {
            if (!visited.has(stageName)) {
                if (hasCycleDFS(stageName)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Builds adjacency list from transitions
     */
    private static buildAdjacencyList(transitions: WorkflowTypes.WorkflowStageTransition[]): Map<string, string[]> {
        const adjacencyList = new Map<string, string[]>();

        transitions.forEach((t) => {
            if (t.fromStageName && t.toStageName) {
                if (!adjacencyList.has(t.fromStageName)) {
                    adjacencyList.set(t.fromStageName, []);
                }
                adjacencyList.get(t.fromStageName)!.push(t.toStageName);
            }
        });

        return adjacencyList;
    }

    /**
     * Detects if adding a transition would create a cycle in the workflow
     */
    static wouldCreateCycle(
        transitions: WorkflowTypes.WorkflowStageTransition[],
        newTransition: WorkflowTypes.WorkflowStageTransition,
    ): boolean {
        if (!newTransition.fromStageName || !newTransition.toStageName) {
            return false;
        }

        // Build adjacency list for all transitions including the new one
        const adjacencyList = this.buildAdjacencyList(transitions.filter((t) => t !== newTransition));

        // Add the new transition
        if (!adjacencyList.has(newTransition.fromStageName)) {
            adjacencyList.set(newTransition.fromStageName, []);
        }
        adjacencyList.get(newTransition.fromStageName)!.push(newTransition.toStageName);

        return this.detectCycleInGraph(adjacencyList);
    }

    /**
     * Detects if the entire workflow has cycles
     */
    static hasCycle(transitions: WorkflowTypes.WorkflowStageTransition[]): boolean {
        if (transitions.length === 0) {
            return false;
        }

        const adjacencyList = this.buildAdjacencyList(transitions);
        return this.detectCycleInGraph(adjacencyList);
    }

    /**
     * Validates FSM constraints: each stage can have at most one transition per type
     */
    static validateFSMConstraints(
        transitions: WorkflowTypes.WorkflowStageTransition[],
        newTransition: WorkflowTypes.WorkflowStageTransition,
    ): string[] {
        const errors: string[] = [];

        if (!newTransition.fromStageName || !newTransition.transitionType) {
            return errors;
        }

        // Check if there's already a transition of the same type from the same stage
        const existingTransition = transitions.find(
            (t) =>
                t.fromStageName === newTransition.fromStageName &&
                t.transitionType === newTransition.transitionType &&
                t !== newTransition &&
                (t.workflowStageTransitionId !== newTransition.workflowStageTransitionId || t.id !== newTransition.id),
        );

        if (existingTransition) {
            errors.push(
                `Stage "${newTransition.fromStageName}" already has a ${newTransition.transitionType.toLowerCase()} transition. ` +
                    `Each stage can only have one transition per type (approve/deny/request_changes).`,
            );
        }

        return errors;
    }

    /**
     * Gets all validation errors for a transition including FSM and cycle constraints
     */
    static getTransitionValidationErrors(
        transitions: WorkflowTypes.WorkflowStageTransition[],
        stages: WorkflowTypes.WorkflowStage[],
        transition: WorkflowTypes.WorkflowStageTransition,
    ): string[] {
        const errors: string[] = [];

        // Basic validation
        if (!transition.transitionLabel?.trim()) {
            errors.push("Transition label is required");
        } else {
            // Check for duplicate transition labels
            const isDuplicate = transitions.some(
                (t) =>
                    t.transitionLabel === transition.transitionLabel &&
                    t !== transition &&
                    (t.workflowStageTransitionId !== transition.workflowStageTransitionId || t.id !== transition.id),
            );

            if (isDuplicate) {
                errors.push(
                    `Transition label "${transition.transitionLabel}" already exists. Please choose a unique name.`,
                );
            }
        }

        if (!transition.fromStageName) {
            errors.push("From stage is required");
        }

        if (!transition.toStageName) {
            errors.push("To stage is required");
        }

        if (transition.fromStageName === transition.toStageName) {
            errors.push("From stage and to stage cannot be the same");
        }

        // Check if referenced stages exist
        if (transition.fromStageName && !stages.find((s) => s.stageName === transition.fromStageName)) {
            errors.push(`From stage "${transition.fromStageName}" does not exist`);
        }
        if (transition.toStageName && !stages.find((s) => s.stageName === transition.toStageName)) {
            errors.push(`To stage "${transition.toStageName}" does not exist`);
        }

        // Only validate transition logic if both stages exist
        const fromStage = stages.find((s: WorkflowTypes.WorkflowStage) => s.stageName === transition.fromStageName);
        const toStage = stages.find((s: WorkflowTypes.WorkflowStage) => s.stageName === transition.toStageName);

        if (fromStage && toStage) {
            if (fromStage.isTerminal) {
                errors.push("Cannot create transitions from terminal stages");
            }

            if (toStage.isStartStage) {
                errors.push("Cannot create transitions to the start stage");
            }

            // Check for cycles
            if (this.wouldCreateCycle(transitions, transition)) {
                errors.push("This transition would create a cycle in the workflow");
            }

            // Check FSM constraints
            const fsmErrors = this.validateFSMConstraints(transitions, transition);
            errors.push(...fsmErrors);
        }

        return errors;
    }

    /**
     * Generate field code from field label
     * Used by workflow components to create consistent field codes
     */
    static generateFieldCode(fieldLabel: string): string {
        if (!fieldLabel) return "";

        // Convert field name to a code-friendly format with max 32 characters
        // Backend regex only allows [A-Za-z0-9], so remove spaces entirely (no underscores)
        const code = fieldLabel
            .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters
            .replace(/\s+/g, "") // Remove spaces entirely (no underscores)
            .toUpperCase();

        // Limit to 32 characters
        return code.length > 32 ? code.substring(0, 32) : code;
    }

    /**
     * Get field code for a field definition, generating if needed
     */
    static getFieldCode(field: IngestionFieldDefinition): string {
        // Use existing fieldCode if available, otherwise generate from fieldLabel
        if (field.fieldCode) {
            return field.fieldCode;
        }
        return WorkflowUtil.generateFieldCode(field.fieldLabel || "");
    }

    /**
     * Generate field reference string for a given field
     */
    static getFieldReference(field: IngestionFieldDefinition): string {
        const fieldCode = WorkflowUtil.getFieldCode(field);
        return `$CNET-${field.fieldDefinitionId}-${fieldCode}`;
    }
}
